import { LoginOutlined } from '@ant-design/icons';
import { Button } from 'antd';
import LoginForm from 'auth/components/login-form.component';
import RecoverPassForm from 'auth/components/recover-pass-form.component';
import RegisterForm from 'auth/components/register-form.component';
import ResetPassForm from 'auth/components/reset-pass.component';
import InputSearch from 'catalog/product/components/input-search.component';
import { AuthType } from 'config/constants/auth.constants';
import { RouteName } from 'config/constants/route.constants';
import { AuthTranscription, GeneralTranscription } from 'config/constants/transcription.constant';
import AuthService from 'config/services/auth.service';
import AuthIntentUtils from 'config/utils/auth-intent.util';
import NavigationUtils from 'config/utils/navigation.util';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { useLocation } from 'react-router-dom';
import { RootState } from 'store/index';
import { AuthSlice } from 'store/slices/auth.slice';
import { SearchSlice } from 'store/slices/search.slice';
import { UserSlice } from 'store/slices/user.slice';
import AddressHeaderButton from 'user/components/address/address-header-button.component';
import UserMenu from 'user/components/user-menu.component';
import AppDrawer from '../drawer.component';
import Loading from '../loading.component';
import MiniCart from '../mini-cart.component';
import Pulsate from '../pulsate.component';
import styles from './style.module.scss';

const Header: React.FC = () => {
  const { auth, user, search, cart, purchase } = useSelector((state: RootState) => state);
  const dispatch = useDispatch();
  const location = useLocation();
  const authQueryParams = NavigationUtils.handleAuthQueryParams();
  const searchQueryParams = NavigationUtils.handleSearchQueryParams();

  const [openDrawer, setOpenDrawer] = useState<boolean>(false);
  const [typeAuthForm, setTypeAuthForm] = useState<AuthType>(AuthType.LESS);
  const [drawerTitle, setDrawerTitle] = useState<string>('');

  const selectedAddress = user.userProfile.address?.find((item) => item.selected);

  const navigate = useNavigate();

  const handleShowDrawer = (openDrawer: boolean, authTypeAction: AuthType, forgotPasswordToken: string | null = null) => {
    NavigationUtils.handleAuthTypeAction(authTypeAction, openDrawer, forgotPasswordToken, location);
    setOpenDrawer(openDrawer);
  };

  const handleDrawerTitle = (authTypeAction: AuthType) => {
    const titles: Record<AuthType, string> = {
      [AuthType.RECOVER_PASS]: AuthTranscription.FORM_RECOVER_PASSWORD_TEXT,
      [AuthType.REGISTER]: AuthTranscription.FORM_REGISTER_TEXT,
      [AuthType.RESET_PASS]: AuthTranscription.FORM_RESET_PASSWORD_TEXT,
      [AuthType.LOGIN]: AuthTranscription.FORM_LOGIN_TEXT,
      [AuthType.LESS]: '',
    };
    const drawerTitle = titles[authTypeAction] ?? AuthTranscription.FORM_LOGIN_TEXT;
    setDrawerTitle(drawerTitle);
  };

  const handleToAddress = () => {
    auth.isAuthenticated ?
      navigate(`${RouteName.MY_PROFILE}?tab=${NavigationUtils.AddressUrlTab.MY_ADDRESS}`, NavigationUtils.navigateOptions)
    : handleShowDrawer(true, AuthType.LOGIN);
  };

  const handleCloseAuthDrawer = () => {
    if (!auth.isAuthenticated) AuthIntentUtils.clearIntent();
    handleShowDrawer(false, AuthType.LESS);
  };

  const handleTypeAuthForm = (type: AuthType): React.ReactNode => {
    const forms: Partial<Record<AuthType, () => React.ReactNode>> = {
      [AuthType.REGISTER]: () => <RegisterForm />,
      [AuthType.LOGIN]: () => <LoginForm />,
      [AuthType.RECOVER_PASS]: () => <RecoverPassForm callBack={handleCloseAuthDrawer} />,
      [AuthType.RESET_PASS]: () => <ResetPassForm userProfile={user.userProfile} callBack={handleCloseAuthDrawer} />,
    };
    const renderForm = forms[type];
    return renderForm ? renderForm() : null;
  };

  const handleVerifyJwt = async () => {
    try {
      const verifyJwtResult = await AuthService.verifyJwt();
      dispatch(UserSlice.refreshUserJwtData(verifyJwtResult));
      if (authQueryParams.authType) {
        handleShowDrawer(true, authQueryParams.authType, authQueryParams.forgotPasswordToken);
        setTypeAuthForm(authQueryParams.authType);
      }
    } catch (error) {
      dispatch(UserSlice.resetUserStore());
      handleShowDrawer(false, AuthType.LESS);
      dispatch(AuthSlice.resetAuhStore());
    }
  };

  const evaluateAuthTypeParams = async (): Promise<void> => {
    const isLogin = !auth.isAuthenticated && Boolean(authQueryParams.authType) && authQueryParams.authType === AuthType.LOGIN;
    const isRecoverPass = !auth.isAuthenticated && Boolean(authQueryParams.authType) && authQueryParams.authType === AuthType.RECOVER_PASS;
    const isRegister = !auth.isAuthenticated && Boolean(authQueryParams.authType) && authQueryParams.authType === AuthType.REGISTER;

    const isResetPassword =
      !auth.isAuthenticated &&
      Boolean(authQueryParams.authType) &&
      authQueryParams.authType === AuthType.RESET_PASS &&
      Boolean(authQueryParams.forgotPasswordToken);

    if (isLogin || isRecoverPass || isRegister || isResetPassword) {
      if (isResetPassword && authQueryParams.forgotPasswordToken) {
        dispatch(AuthSlice.refreshAccessToken(authQueryParams.forgotPasswordToken));
        await handleVerifyJwt();
      } else if (authQueryParams.authType) {
        handleDrawerTitle(authQueryParams.authType);
        handleShowDrawer(true, authQueryParams.authType);
        setTypeAuthForm(authQueryParams.authType);
        dispatch(AuthSlice.dispatchAuthType(authQueryParams.authType));
      }
    }
  };

  useEffect(() => {
    if (auth.isAuthenticated) {
      setOpenDrawer(false);
      const { authType, forgotPasswordToken, redirectTo } = authQueryParams;
      if (redirectTo) return;
      if ((authType && authType !== AuthType.LESS) || (forgotPasswordToken && forgotPasswordToken !== AuthType.LESS)) {
        NavigationUtils.handleAuthTypeAction(AuthType.LESS, false, null, location);
      }
    }
  }, [auth.isAuthenticated]);

  useEffect(() => {
    if (location.search !== '') {
      evaluateAuthTypeParams();
    }
  }, [authQueryParams.authType]);

  useEffect(() => {
    location.pathname === RouteName.SEARCH ?
      dispatch(SearchSlice.refreshSearchStore({ ...search, ...searchQueryParams }))
    : dispatch(SearchSlice.resetSearchStore());
  }, [location.pathname]);

  return (
    <header className={styles['header']}>
      <div className={styles['header-nav-items-container']}>
        <div className={styles['logo']}>
          <a href='/'>
            <img src='/android-chrome-192x192.png' alt='my-alt' width={70} height={70} />
          </a>
        </div>
        <div className={styles['search']}>
          <InputSearch search={search} />
        </div>
        <div className={styles['address']}>
          <AddressHeaderButton selectedAddress={selectedAddress} handleClick={handleToAddress} />
        </div>
        <div className={styles['auth']}>
          {!auth.isAuthenticated && (
            <Button type='default' onClick={() => handleShowDrawer(true, AuthType.LOGIN)}>
              <LoginOutlined />
              {AuthTranscription.FORM_LOGIN_TEXT}
            </Button>
          )}
          {!auth.isFetching && auth.isAuthenticated && <UserMenu user={user} purchase={purchase} />}
          {auth.isFetching && <Pulsate width='150px' height='30px' marginBottom='-8' />}
        </div>
        <div className={styles['cart']}>
          <MiniCart cart={cart} userProfile={user.userProfile} auth={auth} />
        </div>
      </div>
      <AppDrawer title={drawerTitle} isOpen={openDrawer} onClose={handleCloseAuthDrawer}>
        <Loading rotate={auth.isFetching} text={GeneralTranscription.LOADING_TEXT}>
          {typeAuthForm && handleTypeAuthForm(typeAuthForm)}
        </Loading>
      </AppDrawer>
    </header>
  );
};

export default Header;
